import React, { useState } from "react";
import { motion } from "framer-motion";
import { FaFacebook, FaInstagram, FaLinkedin, FaLaptopCode, FaCloud, FaShieldAlt, FaUsers, FaStar } from "react-icons/fa";

const PRIMARY = "rgb(78, 97, 200)"; // main brand color

export default function App() {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", company: "", message: "" });
  const [status, setStatus] = useState("");

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleSubmit(e) {
    e.preventDefault();
    setStatus("Sending...");
    // placeholder: integrate EmailJS or backend here
    setTimeout(() => {
      setStatus("Message sent. We'll contact you soon.");
      setForm({ name: "", email: "", mobile: "", company: "", message: "" });
      setTimeout(() => setStatus(""), 4000);
    }, 900);
  }

  return (
    <div className="font-sans bg-white text-gray-900">
      {/* NAV / HERO */}
      <header className="fixed w-full z-50 bg-white/80 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <a href="#home" className="flex items-center gap-3">
            <img src="/images/trans_logo.png" alt="logo" className="h-10 w-auto" />
            <span className="font-bold text-lg">PHPD Hiring & IT</span>
          </a>

          <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
            <a href="#home" className="hover:text-[rgb(78,97,200)]">Home</a>
            <a href="#about" className="hover:text-[rgb(78,97,200)]">About</a>
            <a href="#services" className="hover:text-[rgb(78,97,200)]">Services</a>
            <a href="#portfolio" className="hover:text-[rgb(78,97,200)]">Portfolio</a>
            <a href="#team" className="hover:text-[rgb(78,97,200)]">Team</a>
            <a href="#contact" className="hover:text-[rgb(78,97,200)]">Contact</a>
          </nav>

          <div className="flex items-center gap-3">
            <a href="#contact" className="hidden md:inline-block px-4 py-2 rounded-full text-white font-semibold" style={{ background: PRIMARY }}>Get a Quote</a>
            <div className="flex items-center gap-2 text-[rgb(78,97,200)] text-lg">
              <a href="https://www.facebook.com/profile.php?id=61566381261583&mibextid=ZbWKwL" target="_blank" rel="noopener noreferrer"><FaFacebook /></a>
              <a href="https://www.instagram.com/phpd_private_limited?igsh=MXJvZ2s3YTFsMGw5cQ==" target="_blank" rel="noopener noreferrer"><FaInstagram /></a>
              <a href="https://www.linkedin.com/company/phpd-hiring-staffing-private-limited/" target="_blank" rel="noopener noreferrer"><FaLinkedin /></a>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24">
        <section id="home" className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ x: -50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.7 }}>
              <p className="text-sm text-gray-600 mb-2">Welcome to</p>
              <h1 className="text-4xl md:text-5xl font-extrabold leading-tight" style={{ color: PRIMARY }}>PHPD IT & Hiring Services</h1>
              <p className="text-lg text-gray-700 mt-4 max-w-xl">We connect great companies with expert talent — and now provide end-to-end IT services: custom software, cloud, security, and managed IT. One partner for people + products.</p>

              <div className="mt-6 flex gap-4">
                <a href="#contact" className="inline-block px-6 py-3 rounded-full text-white font-semibold shadow" style={{ background: PRIMARY }}>Request a Quote</a>
                <a href="#services" className="inline-block px-6 py-3 rounded-full border" style={{ borderColor: PRIMARY, color: PRIMARY }}>Our Services</a>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-4 max-w-sm">
                <Stat icon={<FaUsers />} number={"100+"} label={"Clients"} />
                <Stat icon={<FaStar />} number={"4.8/5"} label={"Satisfaction"} />
              </div>
            </motion.div>

            <motion.div initial={{ x: 50, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.7 }} className="flex justify-center md:justify-end">
              <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-lg bg-white">
                <img src="/images/home.jpg" alt="hero" className="w-full h-72 object-cover" />
                <div className="p-6 text-gray-900">
                  <h3 className="font-semibold text-lg">We build and staff high-quality teams</h3>
                  <p className="mt-2 text-sm">Hire vetted talent or let our IT team build your product. Flexible engagement models.</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* subtle decorative shapes */}
          <svg className="absolute -right-32 top-8 opacity-10" width="400" height="400" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg"><circle cx="200" cy="200" r="200" fill={PRIMARY} /></svg>
        </section>

        {/* ABOUT */}
        <section id="about" className="bg-gray-50 py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-8 items-center">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <img src="/images/team.jpg" alt="team" className="w-full rounded-lg shadow-md" />
            </motion.div>
            <div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: PRIMARY }}>About PHPD Hiring & IT</h2>
              <p className="text-gray-700 mb-4">Founded to bridge talent gaps, PHPD has evolved into a hybrid staffing + IT services firm. We partner with startups and enterprises to deliver hiring, technology, and ongoing support — all under one roof.</p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
                <li>✅ Recruitment & staffing</li>
                <li>✅ Custom software development</li>
                <li>✅ Cloud & DevOps</li>
                <li>✅ Cybersecurity & compliance</li>
              </ul>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl font-bold text-center mb-6" style={{ color: PRIMARY }}>Our Services</h2>
            <p className="text-center max-w-3xl mx-auto text-gray-700 mb-10">Comprehensive offerings across HR and IT — hire talent, build software, secure systems, and run managed services.</p>

            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              <ServiceCard title="Staffing & Recruitment" desc="Permanent, contract & executive hiring with strong screening and fast delivery." icon={<FaUsers />} />
              <ServiceCard title="Executive Search" desc="Find leadership and strategic hires through targeted headhunting." icon={<FaUsers />} />
              <ServiceCard title="Career Counseling" desc="Resume reviews, interview coaching and career mapping for candidates." icon={<FaUsers />} />
              <ServiceCard title="Software Development" desc="Web & mobile apps, SaaS platforms, and product engineering." icon={<FaLaptopCode />} />
              <ServiceCard title="Cloud & DevOps" desc="Cloud migration, infra automation, monitoring and cost optimisation." icon={<FaCloud />} />
              <ServiceCard title="IT Security" desc="Pen tests, vulnerability management, policy and compliance support." icon={<FaShieldAlt />} />
              <ServiceCard title="Managed IT" desc="Ongoing IT support, helpdesk and infrastructure management." icon={<FaUsers />} />
              <ServiceCard title="IT Consulting" desc="Technical strategy, audits, and architecture guidance for growth." icon={<FaUsers />} />
            </div>
          </div>
        </section>

        {/* Portfolio / Case Studies */}
        <section id="portfolio" className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <h2 className="text-3xl font-bold text-center mb-6" style={{ color: PRIMARY }}>Portfolio & Case Studies</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <CaseCard title="Scale Hiring for Fintech" desc="Placed 60+ engineers across backend and frontend in 4 months." img="/images/portfolio1.jpg" />
              <CaseCard title="SaaS Product Build" desc="Built MVP to production for a logistics SaaS, 3x user growth in 6 months." img="/images/portfolio2.jpg" />
              <CaseCard title="Cloud Migration" desc="Migrated monolith to cloud, reduced infra cost by 35%." img="/images/portfolio3.jpg" />
            </div>
          </div>
        </section>

        {/* Pricing / Packages */}
        <section id="pricing" className="py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-3xl font-bold mb-6" style={{ color: PRIMARY }}>Flexible Engagements</h2>
            <p className="text-gray-700 mb-8">From hourly consultations to dedicated teams — pick a model that suits your roadmap and budget.</p>

            <div className="grid md:grid-cols-3 gap-6">
              <motion.div whileHover={{ y: -6 }} className="bg-white rounded-lg shadow p-6 text-left">
                <h3 className="font-semibold text-xl" style={{ color: PRIMARY }}>Starter</h3>
                <p className="text-gray-700 mt-2">Ideal for small projects and short-term hiring.</p>
                <ul className="mt-4 text-gray-700 list-disc list-inside">
                  <li>Consultation</li>
                  <li>Small team (2-4)</li>
                  <li>Monthly billing</li>
                </ul>
              </motion.div>

              <motion.div whileHover={{ y: -6 }} className="bg-white rounded-lg shadow p-6 text-left">
                <h3 className="font-semibold text-xl" style={{ color: PRIMARY }}>Growth</h3>
                <p className="text-gray-700 mt-2">For growing startups building product-market fit.</p>
                <ul className="mt-4 text-gray-700 list-disc list-inside">
                  <li>Dedicated engineers</li>
                  <li>Flexible contracts</li>
                  <li>SLA support</li>
                </ul>
              </motion.div>

              <motion.div whileHover={{ y: -6 }} className="bg-white rounded-lg shadow p-6 text-left">
                <h3 className="font-semibold text-xl" style={{ color: PRIMARY }}>Enterprise</h3>
                <p className="text-gray-700 mt-2">Large scale delivery, compliance & long-term partnerships.</p>
                <ul className="mt-4 text-gray-700 list-disc list-inside">
                  <li>Dedicated account team</li>
                  <li>Security & compliance</li>
                  <li>Custom SLAs</li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-3xl font-bold mb-6" style={{ color: PRIMARY }}>What Clients Say</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Testimonial quote="Exceptional hiring speed and quality — saved us months." name="Ankit, CTO" />
              <Testimonial quote="Built our product with care and ownership." name="Priya, Founder" />
              <Testimonial quote="Professional, responsive and expert team." name="Rahul, HR" />
            </div>
          </div>
        </section>

        {/* Blog Preview */}
        <section id="blog" className="bg-gray-50 py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-3xl font-bold mb-6" style={{ color: PRIMARY }}>Insights & Articles</h2>
            <p className="text-gray-700 mb-8">Latest thinking on hiring, engineering and cloud operations.</p>
            <div className="grid md:grid-cols-3 gap-6">
              <BlogCard title="How to hire remote engineers" excerpt="Best practices to source and retain remote engineering talent." />
              <BlogCard title="Cloud migration checklist" excerpt="A practical checklist for safe and cost-effective migration." />
              <BlogCard title="Security essentials" excerpt="Key steps to secure your web applications and infra." />
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-bold mb-4" style={{ color: PRIMARY }}>Get In Touch</h2>
              <p className="text-gray-700 mb-6">Tell us about your hiring or IT project — we’ll propose a plan and timeline.</p>

              <div className="space-y-4 text-gray-700">
                <p className="flex items-center gap-3"><img src="/images/icons/email.svg" alt="email" className="h-5"/> info@phpdhires.com</p>
                <p className="flex items-center gap-3"><img src="/images/icons/phone.svg" alt="phone" className="h-5"/> +91 9168383504</p>
                <div className="flex gap-4 text-[rgb(78,97,200)] text-xl">
                  <a href="https://www.facebook.com/profile.php?id=61566381261583&mibextid=ZbWKwL" target="_blank" rel="noopener noreferrer"><FaFacebook /></a>
                  <a href="https://www.instagram.com/phpd_private_limited?igsh=MXJvZ2s3YTFsMGw5cQ==" target="_blank" rel="noopener noreferrer"><FaInstagram /></a>
                  <a href="https://www.linkedin.com/company/phpd-hiring-staffing-private-limited/" target="_blank" rel="noopener noreferrer"><FaLinkedin /></a>
                </div>
              </div>
            </div>

            <motion.form onSubmit={handleSubmit} initial={{ y: 20, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }} className="bg-white rounded-lg shadow p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input name="name" value={form.name} onChange={handleChange} placeholder="Name" className="border p-3 rounded" required />
                <input name="mobile" value={form.mobile} onChange={handleChange} placeholder="Mobile" className="border p-3 rounded" required />
                <input name="email" value={form.email} onChange={handleChange} placeholder="Email" className="border p-3 rounded" required />
                <input name="company" value={form.company} onChange={handleChange} placeholder="Company" className="border p-3 rounded" />
              </div>
              <textarea name="message" value={form.message} onChange={handleChange} placeholder="Message" className="border p-3 rounded mt-3" rows={5} required />
              <div className="flex items-center justify-between mt-4">
                <button type="submit" className="px-6 py-2 rounded-full text-white font-semibold" style={{ background: PRIMARY }}>Send Message</button>
                <span className="text-sm text-gray-600">{status}</span>
              </div>
            </motion.form>
          </div>
        </section>
      </main>

      <footer className="bg-gray-100 py-8 text-center text-gray-700">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>© {new Date().getFullYear()} PHPD IT & Hiring Pvt. Ltd.</div>
            <div className="flex gap-4 text-[rgb(78,97,200)]">
              <a href="https://www.facebook.com/profile.php?id=61566381261583&mibextid=ZbWKwL" target="_blank" rel="noopener noreferrer"><FaFacebook /></a>
              <a href="https://www.instagram.com/phpd_private_limited?igsh=MXJvZ2s3YTFsMGw5cQ==" target="_blank" rel="noopener noreferrer"><FaInstagram /></a>
              <a href="https://www.linkedin.com/company/phpd-hiring-staffing-private-limited/" target="_blank" rel="noopener noreferrer"><FaLinkedin /></a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Stat({ icon, number, label }) {
  return (
    <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow">
      <div className="text-[rgb(78,97,200)] text-xl">{icon}</div>
      <div>
        <div className="font-bold">{number}</div>
        <div className="text-sm text-gray-600">{label}</div>
      </div>
    </div>
  );
}

function ServiceCard({ title, desc, icon }) {
  return (
    <motion.div whileHover={{ y: -6 }} className="bg-white rounded-lg shadow p-6 text-center">
      <div className="text-[rgb(78,97,200)] text-2xl mb-3">{icon}</div>
      <h3 className="font-semibold text-lg" style={{ color: PRIMARY }}>{title}</h3>
      <p className="text-gray-700 mt-2">{desc}</p>
    </motion.div>
  );
}

function CaseCard({ title, desc, img }) {
  return (
    <motion.div whileHover={{ scale: 1.02 }} className="bg-white rounded-lg overflow-hidden shadow">
      <img src={img} alt={title} className="w-full h-44 object-cover" />
      <div className="p-4 text-gray-900">
        <h4 className="font-semibold">{title}</h4>
        <p className="text-sm mt-2">{desc}</p>
      </div>
    </motion.div>
  );
}

function ProfileCard({ name, role, img }) {
  return (
    <motion.div whileHover={{ y: -6 }} className="bg-white rounded-lg shadow p-4 text-center">
      <img src={img} alt={name} className="w-28 h-28 rounded-full mx-auto object-cover" />
      <h4 className="font-semibold mt-3">{name}</h4>
      <p className="text-sm text-gray-600">{role}</p>
    </motion.div>
  );
}

function Testimonial({ quote, name }) {
  return (
    <motion.div whileHover={{ scale: 1.02 }} className="bg-white rounded-lg shadow p-6 text-left">
      <p className="text-gray-800">"{quote}"</p>
      <p className="mt-4 font-semibold">{name}</p>
    </motion.div>
  );
}

function BlogCard({ title, excerpt }) {
  return (
    <motion.div whileHover={{ y: -6 }} className="bg-white rounded-lg shadow p-6 text-left">
      <h4 className="font-semibold">{title}</h4>
      <p className="text-sm text-gray-700 mt-2">{excerpt}</p>
      <a href="#" className="inline-block mt-4 text-[rgb(78,97,200)] font-semibold">Read →</a>
    </motion.div>
  );
}
